<template>
  <hb-basic-page
    :title="id ? $t('daybook.edit_daybook') : $t('daybook.new_daybook')"
    :loading="loading"
    :fill-height="loading"
    :back-button="backButton"
    :extension-height="47"
  >
    <v-col md="8" offset-md="2" lg="8" offset-lg="2" class="gradient-icons" :style="$vuetify.breakpoint.xsOnly && 'margin-bottom: 55px;'">
      <v-card class="mb-5">
        <v-card-title class="subtitle-1 text-uppercase font-weight-medium v-card-title-gradient"
          >{{ $tc('daybook.title', 0) }}
        </v-card-title>
        <v-card-text>
          <site-combobox
            :saving="saving || true"
            :multiple="false"
            :label="$t('daybook.site')"
            :sites="sites"
            :value="daybook.site"
            :readonly="true"
            class="mt-5"
            @change="handleSiteChange"
          />
          <date-picker
            :input-styles="{ outlined: true, class: 'mb-5' }"
            :date="daybook.daybookDate"
            :label="$t('daybook.daybook_date')"            @change="
              value => {
                handleDaybookDateChange(value, '');
              }
            "
          ></date-picker>

          <v-autocomplete
            :key="workSelectKey"
            :items="works"
            :label="$t('daybook.work')"
            :value="daybook.work"
            item-text="title"
            item-value="id"
            return-object
            clearable
            dense
            outlined
            :class="{ 'mb-5': !showWorkStatus }"
            :no-data-text="$t('daybook.no_works_for_site')"
            @change="handleWorkChange"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-clipboard-list-outline</v-icon>
            </template>
            <template v-slot:selection="{ item }">
              {{ item.title }}
              <work-status-chip :status="item.status" x-small class="ml-2" />
            </template>
            <template v-slot:item="data">
              <v-list-item v-bind="data.attrs" v-on="data.on">
                <v-list-item-content>
                  <v-list-item-title class="d-flex align-center">
                    {{ data.item.title }}
                    <work-status-chip :status="data.item.status" x-small class="ml-2" />
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>

          <!-- Työn tila tallennetaan merkinnän mukana, jotta työn voi merkitä valmiiksi
               poistumatta päiväkirjasta (esim. työaikakirjauksista tultaessa). -->
          <v-select
            v-if="showWorkStatus"
            v-model="workStatus"
            :items="workStatusItems"
            :label="$t('daybook.work_status')"
            :hint="$t('daybook.work_status_hint')"
            persistent-hint
            dense
            outlined
            class="mb-5"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-progress-check</v-icon>
            </template>
          </v-select>

          <!--<item-combobox
            :label="$t('daybook.method')"
            :saving="saving"
            :value="daybook.method"
            :items="methods"
            :multiple="false"
            @change="v => (daybook.method = v)"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-shovel</v-icon>
            </template>
          </item-combobox>-->
        </v-card-text>
        <v-divider></v-divider>
        <v-card-text>
          <div class="daybook-field-column subtitle-1 font-weight-medium text--primary">{{ $t('daybook.entry_times') }}</div>
          <v-row no-gutters class="">
            <entry-times
              ref="entryTimes"
              :current-entry="daybook"
              :dialog="true"
              :contains-lunch="daybook.containsLunch"
              :handle-duration-dialog-save="handleDurationDialogSave"
              :readonly="false"
              class="mt-3 mb-2"
            ></entry-times>
            <v-col cols="12">
              <v-checkbox
                class="daybook-field-column daybook-field-column--checkbox"
                dense
                v-model="daybook.containsLunch"
                :label="$t('daybook.contains_lunch')"
              ></v-checkbox>
            </v-col>
          </v-row>
          <!-- Sisennetty tekstikenttien reunaan kuten kuvaosio: otsikot kentän reunan, solujen tekstit kentän tekstin kohdalle. -->
          <div class="daybook-field-column">
            <!-- Ryhmitelty kuten tuntikirjausdialogin kirjauksen tyyppi; kerrotaan mikä tulostuu asiakkaalle. -->
            <div v-if="timeEntryWorkers.length > 0" class="mt-2">
              <div class="subtitle-1 font-weight-medium text--primary">
                {{ $t('daybook.workers_title') }} ({{ timeEntryWorkers.length }} {{ $t('daybook.workers_count') }})
              </div>
              <daybook-time-entries
                v-for="group in timeEntryGroups"
                :key="group.chargeType"
                :charge-type="group.chargeType"
                :workers="group.workers"
              />
            </div>
            <div v-else-if="timeEntryWorkersStatus === 'loading'" class="mt-2 caption text--secondary">
              {{ $t('daybook.time_entries_loading') }}
            </div>
            <div v-else-if="timeEntryWorkersStatus === 'loaded'" class="mt-2 caption text--secondary">
              {{ $t('daybook.no_time_entries') }}
            </div>
            <div v-else-if="timeEntryWorkersStatus === 'error'" class="mt-2 d-flex align-center flex-wrap">
              <span class="caption error--text">{{ $t('daybook.time_entries_load_failed') }}</span>
              <v-btn small text color="primary" class="ml-2" @click="loadTimeEntryWorkers">{{ $t('try_again') }}</v-btn>
            </div>
            <div v-if="daybookEquipments.length > 0" class="mt-4">
              <div class="subtitle-1 font-weight-medium text--primary">{{ $t('daybook.equipments_title') }}</div>
              <!-- Puhelimessa rivit kuten kirjauksissa: kolmisarakkeinen taulukko vuotaisi yli. -->
              <template v-if="$vuetify.breakpoint.xsOnly">
                <template v-for="(eq, idx) in daybookEquipments">
                  <v-divider v-if="idx > 0" :key="`divider-${eq.id}`" />
                  <div :key="eq.id" class="d-flex body-2 py-2">
                    <span>{{ equipmentName(eq) }}</span>
                    <v-spacer />
                    <span class="text--secondary text-no-wrap ml-2">{{ eq.licenseNumber }}</span>
                  </div>
                </template>
              </template>
              <v-simple-table v-else dense class="mt-1">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>{{ $t('daybook.equipment_type') }}</th>
                      <th v-if="daybookEquipmentsHaveDescription">{{ $t('daybook.equipment_description') }}</th>
                      <th>{{ $t('daybook.equipment_license') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="eq in daybookEquipments" :key="eq.id">
                      <td>{{ eq.type && eq.type.name ? eq.type.name : '' }}</td>
                      <td v-if="daybookEquipmentsHaveDescription">{{ eq.description || '' }}</td>
                      <td class="text-no-wrap">{{ eq.licenseNumber || '' }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-text>
          <v-textarea
            :disabled="saving"            outlined
            dense
            rows="4"
            hide-details
            class="mb-5 mt-4"
            :label="$t('daybook.site_situation')"
            light
            item-value=""
            autocomplete="off"
            clearable
            auto-grow
            v-model="daybook.siteSituation"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-progress-question</v-icon>
            </template>
          </v-textarea>
          <joku-situation-draft-button
            :site="daybook.site"
            :work="daybook.work"
            :date="daybook.daybookDate"
            :current-situation="daybook.siteSituation"
            :disabled="saving"
            @draft="text => (daybook.siteSituation = text)"
          />
          <!-- Kiinteä leveys, jotta kuvaruudut ovat samaa kokoa kuin poikkeamarivillä myös isolla
               näytöllä (FileUpload mitoittaa ruudut prosentteina). Sisennys 33px = tekstikentän
               prepend-ikoni 24px + sen marginaali 9px, jolloin osio alkaa kentän vasemmasta reunasta. -->
          <v-row no-gutters>
            <v-col cols="auto" style="margin-left: 33px; width: 255px; max-width: calc(100% - 33px)">
              <div class="caption grey--text">{{ $t('daybook.images') }}</div>
              <!-- key: muokattaessa merkintä latautuu vasta mountin jälkeen, joten id:n vaihtuessa
                   komponentti luodaan uudelleen ja se hakee olemassa olevat kuvat. -->
              <file-upload
                :key="daybook.id || 'new'"
                :disabled="saving"
                object="daybook"
                :object-id="daybook.id"
                :allow-basic-user="true"
                ref="daybookImages"
              ></file-upload>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <!--<v-card-text>
          <v-textarea
            :disabled="saving"
            outlined
            dense
            rows="4"
            hide-details
            class="mb-5 mt-4"
            :label="$tc('daybook.deviation', 2)"
            light
            item-value=""
            autocomplete="off"
            clearable
            auto-grow
            v-model="daybook.deviation"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-image-broken-variant</v-icon>
            </template>
          </v-textarea>
          <v-checkbox class="ml-7" :label="$t('daybook.deviation_report_made')" v-model="daybook.deviationReportMade"></v-checkbox>
        </v-card-text>-->
        <daybook-item
          type="deviation"
          icon="mdi mdi-image-broken-variant"
          :daybook="daybook"
          :daybook-item-template="daybookItemTemplate"
          :delete-message="$t('daybook.deviation_confirm_delete')"
          :label="$tc('daybook.deviation', 0)"
          :saving="saving"
          :enable-files="true"
          ref="deviationDaybookItems"
        />
        <v-divider></v-divider>
        <v-card-text>
          <v-textarea
            :disabled="saving"            outlined
            dense
            rows="4"
            hide-details
            class="mb-5 mt-4"
            :label="$t('daybook.orderer_responsibility_items')"
            light
            item-value=""
            autocomplete="off"
            clearable
            auto-grow
            v-model="daybook.ordererResponsibilityItems"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-folder-star-multiple-outline</v-icon>
            </template>
          </v-textarea>
          <v-textarea
            :disabled="saving"            outlined
            dense
            rows="4"
            hide-details
            class="mb-5 mt-4"
            :label="$t('daybook.reviews_held')"
            light
            item-value=""
            autocomplete="off"
            clearable
            auto-grow
            v-model="daybook.reviewsHeld"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-message-draw</v-icon>
            </template>
          </v-textarea>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-text>
          <!--<item-combobox
            :label="$t('daybook.diameter')"
            :saving="saving"
            :value="daybook.diameter"
            :items="diameters"
            @change="v => (daybook.diameter = v)"
            class="mt-4"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-diameter-outline</v-icon>
            </template>
          </item-combobox>-->

          <template v-if="daybook.site">
            <attribute-form
              v-if="hasWorkMethod"
              ref="attributeForm"
              :defs="dayScopeDefs"
              :value="daybook.attributeValues || []"
              :disabled="saving"
              :cumulative-bases="cumulativeBases"
              @input="setDaybookAttributeValues"
            />
            <template v-if="!hasWorkMethod && daybook.id > 0">
            <div v-for="(drilling, key) in this.daybook.daybookDrillings" :key="key">
              <v-row>
                <v-col>
                  <v-card-title class="gradient-info">
                    {{
                      [
                        getWorkTitle(drilling) || 'Poraus ' + (key + 1),
                        !isNoPipeMethod(drilling.method && drilling.method.name) && drilling.diameter && drilling.diameter.name,
                        drilling.method && drilling.method.name && drilling.method.name.toLowerCase() === 'avarrus' && getDrillingRearingSize(drilling) && getDrillingRearingSize(drilling).name,
                        drilling.method && drilling.method.name,
                      ]
                        .filter(Boolean)
                        .join(' - ')
                    }}</v-card-title
                  >
                </v-col>
              </v-row>
              <v-row v-if="getDrillingLisatieto(drilling)" class="mb-0 mt-0">
                <v-col>
                  <div class="pl-4 pb-2 text--secondary">
                    <v-icon small class="mr-1">mdi mdi-information-outline</v-icon>
                    {{ getDrillingLisatieto(drilling) }}
                  </div>
                </v-col>
              </v-row>
              <v-row class="mb-0">
                <v-col cols="12" md="4">
                  <v-text-field
                    :disabled="true"
                    outlined
                    dense
                    hide-details
                    class=""
                    :label="$t('daybook.drilling_length')"
                    light
                    autocomplete="off"
                    clearable
                    readonly
                    suffix="m"
                    v-model.number="drilling.drillingLength"
                  >
                    <template v-slot:prepend>
                      <v-icon>mdi mdi-arrow-expand-horizontal</v-icon>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col v-if="daybook.site" cols="12" md="4">
                  <numeric-input
                    :disabled="saving"
                    :readonly="false"
                    outlined
                    dense
                    class=""
                    :label="$t('daybook.drilling_length_at_day')"
                    hide-details
                    light
                    autocomplete="off"
                    clearable
                    suffix="m"
                    raw
                    v-model.number="drilling.drillingLengthAtDay"
                    @input="calculateTotalLengths"
                  >
                    <template v-slot:prepend v-if="$vuetify.breakpoint.smAndDown">
                      <v-icon>mdi mdi-arrow-expand-horizontal</v-icon>
                    </template>
                  </numeric-input>
                </v-col>

                <v-col cols="12" md="4">
                  <numeric-input
                    :disabled="saving"
                    :readonly="false"
                    outlined
                    dense
                    hide-detailsordered
                    class=""
                    :label="$t('daybook.of_which_rock')"
                    light
                    item-value=""
                    autocomplete="off"
                    clearable
                    suffix="m"
                    v-model.number="drilling.ofWhichRock"
                  >
                    <template v-slot:prepend v-if="$vuetify.breakpoint.smAndDown">
                      <v-icon>mdi mdi-image-filter-hdr</v-icon>
                    </template>
                  </numeric-input>
                </v-col>
              </v-row>
              <v-row v-if="!isNoPipeMethod(drilling.method && drilling.method.name)" class="mt-0 mb-5">
                <v-col cols="12" md="4">
                  <v-text-field
                    :disabled="true"
                    outlined
                    dense
                    hide-details
                    class=""
                    :label="$t('daybook.pipe_length')"
                    light
                    autocomplete="off"
                    clearable
                    readonly
                    suffix="m"
                    v-model.number="drilling.pipeLength"
                  >
                    <template v-slot:prepend>
                      <v-icon>mdi mdi-pipe</v-icon>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col v-if="daybook.site" cols="12" md="4">
                  <numeric-input
                    :disabled="saving"
                    :readonly="false"
                    outlined
                    dense
                    class=""
                    :label="$t('daybook.pipe_length_at_day')"
                    hide-details
                    light
                    autocomplete="off"
                    clearable
                    suffix="m"
                    raw
                    v-model.number="drilling.pipeLengthAtDay"
                    @input="calculateTotalPipeLengths"
                  >
                    <template v-slot:prepend v-if="$vuetify.breakpoint.smAndDown">
                      <v-icon>mdi mdi-pipe</v-icon>
                    </template>
                  </numeric-input>
                </v-col>
              </v-row>
              <v-row class="mt-0 mb-5">
                <v-col cols="12" md="6">
                  <numeric-input
                    :disabled="saving"
                    :readonly="false"
                    outlined
                    dense
                    hide-details
                    class=""
                    :label="$t('daybook.elevation_top_end')"
                    light
                    item-value=""
                    autocomplete="off"
                    clearable
                    suffix="m"
                    v-model.number="drilling.elevationTopEnd"
                  >
                    <template v-slot:prepend>
                      <v-icon>mdi mdi-elevation-rise</v-icon>
                    </template>
                  </numeric-input>
                  <!--<v-text-field
                    :disabled="saving"
                    outlined
                    dense
                    hide-details
                    class=""
                    :label="$t('daybook.elevation_top_end')"
                    light
                    item-value=""
                    autocomplete="off"
                    clearable
                    suffix="m"
                    v-model.number="drilling.elevationTopEnd"
                  >
                    <template v-slot:prepend>
                      <v-icon>mdi mdi-elevation-rise</v-icon>
                    </template>
                  </v-text-field>-->
                </v-col>
                <v-col cols="12" md="6">
                  <numeric-input
                    :disabled="saving"
                    :readonly="false"
                    outlined
                    dense
                    hide-details
                    class=""
                    :label="$t('daybook.elevation_lower_end')"
                    light
                    item-value=""
                    autocomplete="off"
                    clearable
                    suffix="m"
                    v-model.number="drilling.elevationLowerEnd"
                  >
                    <template v-slot:prepend>
                      <v-icon>mdi mdi-elevation-decline</v-icon>
                    </template>
                  </numeric-input>
                  <!--<v-text-field
                    :disabled="saving"
                    outlined
                    dense
                    hide-details
                    class=""
                    :label="$t('daybook.elevation_lower_end')"
                    light
                    item-value=""
                    autocomplete="off"
                    clearable
                    suffix="m"
                    v-model.number="drilling.elevationLowerEnd"
                  >
                    <template v-slot:prepend v-if="$vuetify.breakpoint.smAndDown">
                      <v-icon>mdi mdi-elevation-decline</v-icon>
                    </template>
                  </v-text-field>-->
                </v-col>
                <v-divider></v-divider>
              </v-row>
            </div>
            </template>
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <daybook-item
          type="bought"
          icon="mdi mdi-cart-arrow-down"
          :daybook="daybook"
          :daybook-item-template="daybookItemTemplate"
          :delete-message="$t('daybook.bought_confirm_delete')"
          :label="$tc('daybook.bought', 0)"
          :saving="saving"
          :enable-files="true"
          ref="boughtDaybookItems"
        />
        <v-divider></v-divider>
        <daybook-item
          type="ordered"
          icon="mdi mdi-shopping-outline"
          :daybook="daybook"
          :daybook-item-template="daybookItemTemplate"
          :delete-message="$t('daybook.ordered_confirm_delete')"
          :label="$tc('daybook.ordered', 0)"
          :saving="saving"
          :enable-files="true"
          ref="orderedDaybookItems"
        />
        <v-divider></v-divider>
        <v-card-text>
          <v-textarea
            :disabled="saving"            outlined
            dense
            rows="4"
            hide-details
            class="mb-5 mt-4"
            :label="$t('daybook.orderer_notices')"
            light
            item-value=""
            autocomplete="off"
            clearable
            auto-grow
            v-model="daybook.ordererNotices"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-message-alert</v-icon>
            </template>
          </v-textarea>
          <v-textarea
            :disabled="saving"            outlined
            dense
            rows="4"
            hide-details
            class="mb-5 mt-4"
            :label="$t('daybook.other')"
            light
            item-value=""
            autocomplete="off"
            clearable
            auto-grow
            v-model="daybook.other"
          >
            <template v-slot:prepend>
              <v-icon>mdi mdi-frequently-asked-questions</v-icon>
            </template>
          </v-textarea>
          <!-- Mobiilissa Peruuta ja Tallenna ovat kiinteitä FAB-nappeja, joten Tyhjennä on tässä. -->
          <v-btn v-if="!id && $vuetify.breakpoint.xsOnly" :disabled="saving" block text @click="clearDaybook">
            <v-icon left small>mdi-eraser</v-icon>
            <span>{{ $t('daybook.clear_daybook') }}</span>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-col>
    <div class="button_group">
      <v-btn v-if="!id && $vuetify.breakpoint.smAndUp" :disabled="saving" text class="mr-3" @click="clearDaybook">
        <v-icon left small>mdi-eraser</v-icon>
        <span>{{ $t('daybook.clear_daybook') }}</span>
      </v-btn>
      <v-btn
        :fab="$vuetify.breakpoint.xsOnly"
        :fixed="$vuetify.breakpoint.xsOnly"
        :bottom="$vuetify.breakpoint.xsOnly"
        :left="$vuetify.breakpoint.xsOnly"
        @click="cancel"
        class="default mr-3"
        ><v-icon v-if="$vuetify.breakpoint.xsOnly">mdi mdi-close</v-icon
        ><span v-if="$vuetify.breakpoint.smAndUp">{{ $t('cancel') }}</span></v-btn
      >
      <v-btn
        :fab="$vuetify.breakpoint.xsOnly"
        :fixed="$vuetify.breakpoint.xsOnly"
        :bottom="$vuetify.breakpoint.xsOnly"
        :right="$vuetify.breakpoint.xsOnly"
        @click="saveDaybook"
        class="primary mr-3"
        :loading="saving"
      >
        <v-icon :left="$vuetify.breakpoint.smAndUp">mdi-content-save</v-icon>
        <span v-if="$vuetify.breakpoint.smAndUp">{{ $t('save') }}</span>
      </v-btn>
    </div>
    <v-dialog v-model="existingDaybookDialog" max-width="500">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ $t('daybook.daybook_exists_title') }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-6 text-body-1">{{ $t('daybook.daybook_exists') }}</v-card-text>
        <v-card-actions>
          <v-btn text @click="cancelExistingDaybook">{{ $t('cancel') }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="goToExistingDaybook">{{ $t('daybook.daybook_exists_go_to_entry') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </hb-basic-page>
</template>

<script>
import siteApi from '@/api/site';
import personApi from '@/api/person';
import workGroupApi from '@/api/workGroup';
import equipmentApi from '@/api/equipment';
import projectApi from '@/api/project';
import workerApi from '@/api/worker';
import daybookApi from '@/api/daybook';
import dayjs from 'dayjs';
import DatePicker from '@/components/DatePicker';
import SiteCombobox from '@/components/SiteCombobox';
import EntryTimes from '@/components/Timer/EntryTimes';
import DaybookItem from '@/components/SiteDaybook/DaybookItem';
import Immutable from 'immutable';
import NumericInput from '@/components/NumericInput';
import AttributeForm from '@/components/Work/AttributeForm';
import WorkStatusChip from '@/components/Work/WorkStatusChip';
import FileUpload from '@/components/FileUpload';
import JokuSituationDraftButton from '@/components/SiteDaybook/JokuSituationDraftButton';
import DaybookTimeEntries from '@/components/SiteDaybook/DaybookTimeEntries';
import { equipmentName, groupTimeEntries, uniqueEquipments } from '@/helpers/daybookTimeEntries';
import workMethodsApi from '@/api/workMethods';
import { hasPurpose, isNoPipeMethod, isPipeType, loadItemTypes, PURPOSE } from '@/helpers/itemTypes';

const daybookItemTemplate = () => {
  return {
    value: '',
    type: '',
    extra: { _internalKey: (Math.random() + 1).toString(36) },
  };
};

const daybookTemplate = {
  site: null,
  work: null,
  attributeValues: [],
  daybookDate: dayjs().startOf('day'),
  method: null,

  entryStart: dayjs().hour(7).minute(0).second(0).millisecond(0),
  entryEnd: dayjs().hour(17).minute(30).second(0).millisecond(0),
  entryStartDate: '',
  entryStartTime: '',
  entryEndDate: '',
  entryEndTime: '',

  entryStartWait: null,
  entryEndWait: null,
  entryStartWaitDate: '',
  entryStartWaitTime: '',
  entryEndWaitDate: '',
  entryEndWaitTime: '',
  entryWaitDescription: '',

  entryStartWait2: null,
  entryEndWait2: null,
  entryStartWait2Date: '',
  entryStartWait2Time: '',
  entryEndWait2Date: '',
  entryEndWait2Time: '',
  entryWait2Description: '',

  entryStartWait3: null,
  entryEndWait3: null,
  entryStartWait3Date: '',
  entryStartWait3Time: '',
  entryEndWait3Date: '',
  entryEndWait3Time: '',
  entryWait3Description: '',

  containsLunch: true,
  additionalWork: '',
  siteSituation: '',
  deviation: '',
  deviationReportMade: false,
  ordererResponsibilityItems: '',
  reviewsHeld: '',
  diameter: null,
  drillingLength: null,
  drillingLengthAtDay: null,
  ofWhichRock: null,
  elevationTopEnd: null,
  elevationLowerEnd: null,
  bought: null,
  ordered: null,
  ordererNotices: null,
  other: null,
  userId: null,
  daybookDrillings: [],
  daybookItems: [
    {
      ...daybookItemTemplate(),
      type: 'deviation',
    },
    {
      ...daybookItemTemplate(),
      type: 'bought',
    },
    {
      ...daybookItemTemplate(),
      type: 'ordered',
    },
    {
      ...daybookItemTemplate(),
      type: 'additionalWork',
    },
  ],
};
const dateFormat = 'YYYY-MM-DD';

// Keskeneräistä merkintää säilytetään korkeintaan viikon.
const DRAFT_TTL_DAYS = 7;
// Kellonajat, jotka luonnos säilyttää. Nimet vastaavat syncEntryTimesAndDates-metodin
// käyttämää entryStartTime${appendix}-muotoa, ei mallin entryStartWaitTime-kenttiä.
const DRAFT_TIME_FIELDS = [
  'entryStartTime',
  'entryEndTime',
  'entryStartTimeWait',
  'entryEndTimeWait',
  'entryStartTimeWait2',
  'entryEndTimeWait2',
  'entryStartTimeWait3',
  'entryEndTimeWait3',
];
// Päivämäärät talletetaan luonnokseen, koska vuoro voi päättyä eri päivänä kuin se alkaa.
// Ne ovat erillään DRAFT_TIME_FIELDSistä, koska syncEntryTimesAndDates kirjoittaa ne
// uusiksi jokaisella päivämäärän vaihdolla: mukana muutosvertailussa pelkkä päivien
// selaaminen loisi luonnoksen.
const DRAFT_DATE_FIELDS = ['entryStartDate', 'entryEndDate'];
// Vapaat tekstikentät, joita uudella merkinnällä voi täyttää.
const DRAFT_TEXT_FIELDS = [
  'siteSituation',
  'ordererResponsibilityItems',
  'reviewsHeld',
  'ordererNotices',
  'other',
  'entryWaitDescription',
  'entryWait2Description',
  'entryWait3Description',
];
const DAYBOOK_ITEM_TYPES = ['deviation', 'bought', 'ordered', 'additionalWork'];
// goToExistingDaybook lataa sivun uudelleen, mikä pyyhkii originRouten muistista.
// Tuloreitti talletetaan siksi sessioon reloadin yli.
const ORIGIN_ROUTE_STORAGE_KEY = 'SiteDaybookView-originRoute';

const daybookDrillingTemplate = {
  extra: null,
  taskOrder: null,
  method: null,
  diameter: null,
  rearingSize: null,
  drillingLength: null,
  drillingLengthAtDay: null,
  ofWhichRock: null,
  pipeLength: null,
  pipeLengthAtDay: null,
  elevationTopEnd: null,
  elevationLowerEnd: null,
};
export default {
  name: 'SiteDaybookView',
  components: {
    NumericInput,
    DaybookItem,
    EntryTimes,
    SiteCombobox,
    DatePicker,
    AttributeForm,
    WorkStatusChip,
    FileUpload,
    JokuSituationDraftButton,
    DaybookTimeEntries,
  },
  props: {
    siteId: { type: [String, Number], required: true },
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      workMethods: [],
      workSelectKey: 0,
      loading: false,
      searchPhrase: '',
      loadingSiteWorkerListReport: false,
      daybookItemTemplate: Immutable.fromJS({ ...daybookItemTemplate() }).toJS(),
      saving: false,
      persons: [],
      workGroups: [],
      equipments: [],
      daybooks: [],
      projects: [],
      workers: [],
      sites: [],
      daybook: Immutable.fromJS({ ...daybookTemplate, userId: this.$userInfo?.id }).toJS(),
      // Päivän tuntikirjaukset haetaan työmaan, päivän ja työn mukaan, myös uudelle merkinnälle.
      timeEntryWorkers: [],
      timeEntryWorkersStatus: null, // null | 'loading' | 'loaded' | 'error'
      timeEntryWorkersRequest: 0,
      selectedWorker: null,
      dialog: false,
      existingDaybookDialog: false,
      existingDaybookId: null,
      // Kumpi valinta päällekkäisyysdialogin laukaisi, jotta peruutus palauttaa oikean.
      existingDaybookTrigger: 'date',
      previousDaybookDate: null,
      previousWork: null,
      // Työn tila, joka tallennetaan merkinnän tallennuksen yhteydessä jos se on muuttunut.
      workStatus: null,
      waitTimes: 1,
      skipDraftSave: false,
      // Luonnos jätetään koskematta poistuttaessa: käyttäjä valitsi kannan tiedot.
      keepDraftOnLeave: false,
      // Mountissa asetetut oletuskellonajat; koskematonta lomaketta ei tallenneta luonnokseksi.
      defaultEntryTimes: null,
      // Kannasta ladatun merkinnän tilannevedos, johon muokkausnäkymän muutoksia verrataan.
      savedDaybookSnapshot: null,
      // Reitti josta merkintään tultiin (esim. työaikakirjaukset), jotta takaisin-nuoli
      // ja tallennus palaavat sinne eivätkä aina työmaalle.
      originRoute: null,
    };
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (from && from.name) {
        vm.originRoute = { name: from.name, params: { ...from.params }, query: { ...from.query } };
      } else {
        // Sivu ladattiin uudelleen (esim. goToExistingDaybook): palautetaan talletettu tuloreitti.
        try {
          const saved = sessionStorage.getItem(ORIGIN_ROUTE_STORAGE_KEY);
          if (saved) vm.originRoute = JSON.parse(saved);
        } catch (e) { /* ignore */ }
      }
      try {
        sessionStorage.removeItem(ORIGIN_ROUTE_STORAGE_KEY);
      } catch (e) { /* ignore */ }
    });
  },
  async mounted() {
    this.loading = true;

    this.daybook = Immutable.fromJS({ ...daybookTemplate, userId: this.$userInfo?.id }).toJS();
    // Vanhan poraustiedon rivien roolit (putki, menetelmä, avarruskoko) luetaan nimiketyypeistä.
    await loadItemTypes();
    await this.getSites();
    if (this.id) {
      await this.getDaybook();
    } else {
      this.resetDaybookFields();
    }

    // Työmenetelmät tarvitaan attribuuttiarvojen palautukseen, joten odotetaan getItems.
    await this.getItems();
    this.getPersons();
    this.getWorkGroups();
    this.getEquipments();
    this.getProjects();
    this.getWorkers();
    // Odotetaan myös päiväkirjat, jotta luonnoksen päivämäärälle tehtävä
    // päällekkäisyystarkistus osuu valmiiseen listaan. Uudella merkinnällä
    // tarkistetaan vasta kun työ on valittu, ei tyhjällä työllä.
    await this.getDaybooks(!!this.id);

    await this.handleDraftOnOpen();

    this.loading = false;
  },
  beforeRouteLeave(to, from, next) {
    // Sivulta poistuttaessa keskeneräinen merkintä talteen (takaisin-nappi, selaimen back, valikko).
    this.saveDraft();
    next();
  },
  beforeDestroy() {
    // Varmistin niille tapauksille, joissa komponentti tuhotaan ilman reittivaihtoa.
    this.saveDraft();
  },
  computed: {
    // Tuloreitti johon takaisin-nuoli ja tallennus palaavat (esim. työaikakirjaukset).
    // Null kun palataan oletukseen eli työmaan Päiväkirjat-välilehdelle: työmaalta
    // tultaessa tai kun tuloreittiä ei tunneta.
    resolvedOriginRoute() {
      return this.originRoute && this.originRoute.name !== 'tenant_site_edit_site' ? this.originRoute : null;
    },
    backButton() {
      const to = this.resolvedOriginRoute || { name: 'tenant_site_edit_site', params: { id: this.siteId, tab: 'daybooks' } };
      return { show: true, to };
    },
    works() {
      const works = (this.daybook.site && this.daybook.site.works) || [];
      return [...works].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    },
    canChangeWorkStatus() {
      return this.$isTenantAdmin || this.$isTenantManager;
    },
    showWorkStatus() {
      return this.canChangeWorkStatus && !!(this.daybook.work && this.daybook.work.id);
    },
    workStatusItems() {
      return ['NOTSTARTED', 'INPROGRESS', 'DONE'].map(s => ({ text: this.$t(`sites.work_statuses.${s}`), value: s }));
    },
    hasWorkMethod() {
      return !!(this.daybook.work && this.daybook.work.workMethodId);
    },
    dayScopeDefs() {
      const methodId = this.daybook.work && this.daybook.work.workMethodId;
      const method = this.workMethods.find(m => m.id === methodId);
      return (method && method.attributes ? method.attributes : []).filter(d => d.scope === 'DAY');
    },
    // defId → työn aiempien päivien summa kumulatiivisille attribuuteille (kuten legacy calculateTotalLengths)
    cumulativeBases() {
      if (!this.hasWorkMethod || !this.daybook.daybookDate) return null;
      const workId = this.daybook.work && this.daybook.work.id;
      const current = dayjs(this.daybook.daybookDate).startOf('day');
      const bases = {};
      this.dayScopeDefs.filter(d => d.cumulative).forEach(def => {
        bases[def.id] = this.daybooks
          .filter(d => d.id !== this.daybook.id && d.work && d.work.id === workId
            && dayjs(d.daybookDate).startOf('day').isBefore(current))
          .reduce((sum, d) => {
            const v = (d.attributeValues || []).find(av => av.defId === def.id);
            return sum + (Number(v && v.valueNumber) || 0);
          }, 0);
      });
      return bases;
    },
    filteredWorkers() {
      const ids = {};
      this.site.workers?.forEach(w => (ids[w.id] = true));
      return this.workers.filter(w => !ids[w.id]);
    },
    deviations() {
      return (this.daybook.daybookItems || []).filter(item => item.type === 'deviation');
    },
    bought() {
      return (this.daybook.daybookItems || []).filter(item => item.type === 'bought');
    },
    ordered() {
      return (this.daybook.daybookItems || []).filter(item => item.type === 'ordered');
    },
    additionalWork() {
      return (this.daybook.daybookItems || []).filter(item => item.type === 'additionalWork');
    },

    siteDrillings() {
      const d = this.daybook.site?.items?.filter(i => i.task === 'drilling') || [];

      const tasks = Array.from(new Set(d.map(i => i.taskOrder)));

      const res = {};
      tasks.forEach(t => {
        res[t] = d.filter(r => r.taskOrder === t);
      });
      const keys = Object.keys(res);
      keys.sort((a, b) => parseInt(a) - parseInt(b));
      const res1 = [];
      keys.forEach(k => {
        res1.push({
          pipe: res[k].find(r => isPipeType(r.itemTypeId)),
          method: res[k].find(r => hasPurpose(r.itemTypeId, PURPOSE.DRILLING_METHOD)),
          rearingSize: res[k].find(r => hasPurpose(r.itemTypeId, PURPOSE.REAMING_SIZE)),
          lisatieto: (res[k].find(r => hasPurpose(r.itemTypeId, PURPOSE.DRILLING_METHOD)) || {}).lisatieto || null,
          task: 'drilling',
          taskOrder: parseInt(k),
        });
      });

      return res1;
    },
    daybookEquipments() {
      return uniqueEquipments(this.timeEntryWorkers);
    },
    daybookEquipmentsHaveDescription() {
      return this.daybookEquipments.some(eq => eq.description);
    },
    timeEntryGroups() {
      return groupTimeEntries(this.timeEntryWorkers);
    },
    timeEntryWorkersQuery() {
      const siteId = this.daybook.site && this.daybook.site.id;
      const date = this.daybook.daybookDate && dayjs(this.daybook.daybookDate);
      if (!siteId || !date || !date.isValid()) return null;
      return {
        key: [siteId, date.format('YYYY-MM-DD'), (this.daybook.work && this.daybook.work.id) || ''].join('|'),
        siteId,
        date,
        workId: (this.daybook.work && this.daybook.work.id) || null,
      };
    },
  },

  methods: {
    equipmentName,
    isNoPipeMethod(methodName) {
      return isNoPipeMethod(methodName);
    },
    getDrillingRearingSize(drilling) {
      if (drilling.rearingSize) return drilling.rearingSize;
      const siteDrilling = this.siteDrillings.find(d => d.taskOrder === drilling.taskOrder);
      return siteDrilling?.rearingSize?.item || null;
    },
    getDrillingLisatieto(drilling) {
      const siteDrilling = this.siteDrillings.find(d => d.taskOrder === drilling.taskOrder);
      return siteDrilling?.lisatieto || null;
    },
    getWorkTitle(drilling) {
      const work = (this.daybook.site?.works || []).find(w => w.sortOrder === drilling.taskOrder);
      return work && work.title ? work.title : null;
    },
    log(...args) {
      console.log(args);
    },
    async loadTimeEntryWorkers() {
      const query = this.timeEntryWorkersQuery;
      // Vanhentunut vastaus (päivä tai työ vaihtui kesken haun) ei saa korvata uudempaa.
      const request = ++this.timeEntryWorkersRequest;
      this.timeEntryWorkers = [];
      if (!query) {
        this.timeEntryWorkersStatus = null;
        return;
      }
      this.timeEntryWorkersStatus = 'loading';
      try {
        const workers = await daybookApi.getTimeEntries(query.siteId, query.date, query.workId);
        if (request !== this.timeEntryWorkersRequest) return;
        this.timeEntryWorkers = workers;
        this.timeEntryWorkersStatus = 'loaded';
      } catch (e) {
        if (request !== this.timeEntryWorkersRequest) return;
        console.error(e);
        this.timeEntryWorkersStatus = 'error';
      }
    },
    getClosestHalfHourInterval() {
      const nowMinutes = dayjs().minute();
      if (nowMinutes < 45) {
        return dayjs().format('HH:') + (nowMinutes > 15 ? '30' : '00');
      } else {
        return dayjs().add(1, 'hours').format('HH:') + '00';
      }
    },
    syncEntryTimesAndDates(daybook, appendix = '') {
      if (!daybook[`entryStart${appendix}`]) {
        daybook[`entryStartDate${appendix}`] = dayjs(daybook.daybookDate).format(dateFormat);
        if (appendix === 'Wait' || appendix === 'Wait2' || appendix === 'Wait3') {
          daybook[`entryStartTime${appendix}`] = '';
        } else {
          daybook[`entryStartTime${appendix}`] = this.getClosestHalfHourInterval();
        }
      } else {
        daybook[`entryStartDate${appendix}`] = daybook[`entryStart${appendix}`].format(dateFormat);
        daybook[`entryStartTime${appendix}`] = daybook[`entryStart${appendix}`].format('HH:mm');
      }
      if (!daybook[`entryEnd${appendix}`]) {
        daybook[`entryEndDate${appendix}`] = dayjs(daybook.daybookDate).format(dateFormat);
      } else {
        daybook[`entryEndDate${appendix}`] = daybook[`entryEnd${appendix}`].format(dateFormat);
        daybook[`entryEndTime${appendix}`] = daybook[`entryEnd${appendix}`].format('HH:mm');
      }
      this.handleTimeChangeInDurationDialog(appendix);
    },

    handleTimeChangeInDurationDialog(appendix = '') {
      this.$nextTick(() => {
        this.$refs[`entryTimes${appendix}`]?.$refs?.durationDialog?.updateTimes(appendix);
      });
    },
    handleDurationDialogSave(end, appendix) {
      this.daybook[`entryEnd${appendix}`] = dayjs(end);
      this.daybook[`entryEndDate${appendix}`] = dayjs(end).format(dateFormat);
      this.daybook[`entryEndTime${appendix}`] = dayjs(end).format('HH:mm');
    },

    handleDaybookDateChange(value, appendix = '') {
      this.previousDaybookDate = this.daybook.daybookDate;
      this.daybook.daybookDate = dayjs(value).clone().startOf('day');
      if (value) {
        const v = dayjs(value);

        if (!this.daybook[`entryStart${appendix}`]) {
          return;
        }
        const start = dayjs(this.daybook[`entryStart${appendix}`]);
        const end = dayjs(this.daybook[`entryEnd${appendix}`]);

        // Yön yli jatkuvan vuoron lopetus on eri päivänä kuin aloitus, joten siirretään
        // loppu samalla päiväerotuksella eikä pakoteta merkinnän päiväksi.
        const dayOffset =
          start.isValid() && end.isValid() ? end.startOf('day').diff(start.startOf('day'), 'day') : 0;

        if (start && start.isValid()) {
          this.daybook[`entryStart${appendix}`] = dayjs(
            `${v.year()}-${v.month() + 1}-${v.date()} ${start.hour()}:${start.minute()}:00`,
            'YYYY-M-D H:m:s'
          );
        }
        if (end && end.isValid()) {
          this.daybook[`entryEnd${appendix}`] = dayjs(
            `${v.year()}-${v.month() + 1}-${v.date()} ${end.hour()}:${end.minute()}:00`,
            'YYYY-M-D H:m:s'
          ).add(dayOffset, 'day');
        }
      }
      this.syncEntryTimesAndDates(this.daybook, appendix);
      this.checkExistingDaybookForDate();
    },
    handleSiteChange(value) {
      this.daybook.site = value;
    },
    handleWorkChange(work) {
      // Uusilla merkinnöillä työ vaihtuu suoraan. Olemassa olevalla merkinnällä, jolla on jo
      // päivätietoa, varoitetaan ennen vaihtoa: muille töille kuuluvat tiedot voivat poistua.
      const isExisting = this.daybook.id > 0;
      const hasData =
        (this.daybook.attributeValues && this.daybook.attributeValues.length > 0) ||
        (this.daybook.daybookDrillings && this.daybook.daybookDrillings.length > 0);
      if (!isExisting || !hasData) {
        this.applyWorkChange(work);
        return;
      }
      this.$confirm(
        'warning',
        'Aseta työ',
        'Työn asettaminen tälle merkinnälle voi poistaa muille töille kuuluvia tietoja. Haluatko jatkaa?'
      ).then(ok => {
        if (ok) {
          this.applyWorkChange(work);
        } else {
          // Peruutus: daybook.work jätetään ennalleen, palautetaan valitsimen näkymä.
          this.workSelectKey++;
        }
      });
    },
    applyWorkChange(work) {
      this.previousWork = this.daybook.work || null;
      this.$set(this.daybook, 'work', work || null);
      this.workStatus = (work && work.status) || null;
      // Säilytetään vain valitun työn menetelmän DAY-määrittelyihin kuuluvat arvot.
      const method = work && this.workMethods.find(m => m.id === work.workMethodId);
      const dayDefIds = method && method.attributes
        ? method.attributes.filter(d => d.scope === 'DAY').map(d => d.id)
        : [];
      const kept = (this.daybook.attributeValues || []).filter(v => dayDefIds.includes(v.defId));
      this.$set(this.daybook, 'attributeValues', kept);
      // Legacy-porausrivit koskevat vain olemassa olevia (migroituja) merkintöjä; uudet käyttävät
      // pelkkää AttributeFormia, joten niille ei luoda porausrivejä.
      if (this.daybook.id > 0) {
        if (work) {
          // Yksi merkintä = yksi työ: rajataan porausrivit valittuun työhön (legacy-näkymä).
          this.daybook.daybookDrillings = (this.daybook.daybookDrillings || []).filter(dd => dd.taskOrder === work.sortOrder);
        }
        this.addDaybookDrillings();
      }
      // Merkintä on työkohtainen, joten päällekkäisyys on tarkistettava myös työn vaihtuessa,
      // ei vain päivämäärän. Työ valitaan usein vasta päivämäärän jälkeen.
      this.checkExistingDaybookForDate('work');
    },
    setDaybookAttributeValues(values) {
      this.$set(this.daybook, 'attributeValues', values);
    },
    checkIfAddedDaybookItemImageWithoutValue(refKey) {
      if (this.$refs[refKey] && this.$refs[refKey].$refs && this.daybook?.id > 0) {
        const refKeys = Object.keys(this.$refs[refKey].$refs);
        for (let i = 0; i < refKeys.length; i++) {
          const r = refKeys[i];
          if (r.indexOf('images_') > -1) {
            const item = this.daybook.daybookItems.find(
              item => item?.extra?._internalKey === this.$refs[refKey].$refs[r]?.[0]?.item?.extra?._internalKey
            );
            if (item && !item.value && this.$refs[refKey].$refs[r]?.[0]?.addedFiles?.length > 0) {
              this.$showWarningNotification(this.$t('daybook.warning_add_value_for_deviation_with_image'));
              return false;
            }
          }
        }
      }
      return true;
    },
    async uploadDaybookItemImages(daybook, refKey) {
      if (this.$refs[refKey] && this.$refs[refKey].$refs && daybook?.id > 0) {
        const refKeys = Object.keys(this.$refs[refKey].$refs);

        for (let i = 0; i < refKeys.length; i++) {
          const r = refKeys[i];
          if (r.indexOf('images_') > -1) {
            const item = daybook.daybookItems.find(
              item => item?.extra?._internalKey === this.$refs[refKey].$refs[r]?.[0]?.item?.extra?._internalKey
            );
            if (item?.id > 0) {
              await this.$refs[refKey].$refs[r]?.[0]?.uploadFiles?.(item.id);
            }
          }
        }
      }
    },
    createChangedEntryDate(dateType = 'start', appendix = '') {
      const key = `entry${dateType.capitalize()}${appendix}`;

      if (this.daybook[`entry${dateType.capitalize()}Time${appendix}`] === null) {
        this.daybook[key] = null;
      } else {
        // Päivämäärää ei pakoteta merkinnän päiväksi: työvuoro voi jatkua yön yli,
        // jolloin lopetuspäivä on eri kuin aloituspäivä. Tyhjä kenttä täytetään vain siltä
        // varalta, ettei syncEntryTimesAndDates ole vielä ehtinyt ajaa: lopetus perii tällöin
        // aloituspäivän, ei merkinnän päivää, jottei yön yli jatkuva vuoro romahda samalle päivälle.
        const dateKey = `entry${dateType.capitalize()}Date${appendix}`;
        if (!this.daybook[dateKey]) {
          const fallback =
            dateType === 'end'
              ? this.daybook[`entryStartDate${appendix}`] || dayjs(this.daybook.daybookDate).format(dateFormat)
              : dayjs(this.daybook.daybookDate).format(dateFormat);
          this.daybook[dateKey] = fallback;
        }
        const d = dayjs(
          `${this.daybook[dateKey]} ${this.daybook[`entry${dateType.capitalize()}Time${appendix}`]}`
        );
        if (d.isValid()) this.daybook[key] = d;
      }

      this.handleTimeChangeInDurationDialog(appendix);
    },
    draftStorageKey() {
      const userId = this.$userInfo && this.$userInfo.id;
      // $tenant on yleensä id-merkkijono, mutta varapolussa se voi olla tenant-olio.
      const tenantId = (this.$tenant && this.$tenant.id) || this.$tenant;
      if (!tenantId || !userId || !this.siteId) return null;
      // Työmaakohtainen: eri työmaiden keskeneräiset merkinnät ovat eri asioita.
      return `SiteDaybookView.vue-draft-${tenantId}-${userId}-${this.siteId}`;
    },
    currentEntryTimes() {
      const times = {};
      DRAFT_TIME_FIELDS.forEach(field => (times[field] = this.daybook[field] || ''));
      return times;
    },
    hasDraftContent() {
      const d = this.daybook;
      if (d.work) return true;
      if (DRAFT_TEXT_FIELDS.some(field => d[field])) return true;
      if ((d.daybookItems || []).some(item => item.value)) return true;
      const hasAttributeValue = (d.attributeValues || []).some(
        v =>
          (v.valueNumber !== null && v.valueNumber !== undefined) ||
          v.valueText ||
          // Tietoisesti pois rastitettu valinta on false, joten pelkkä totuusarvotesti ei riitä.
          (v.valueBoolean !== null && v.valueBoolean !== undefined) ||
          v.valueDate ||
          v.valueItem
      );
      if (hasAttributeValue) return true;
      if (d.containsLunch !== true) return true;
      if (d.deviationReportMade !== false) return true;
      // Päivämäärän vaihtaminen ei yksin riitä, muuten pelkkä päivien selaaminen loisi luonnoksen.
      const current = this.currentEntryTimes();
      const defaults = this.defaultEntryTimes || {};
      return DRAFT_TIME_FIELDS.some(field => current[field] !== defaults[field]);
    },
    saveDraft() {
      // Ei luonnosta tallennuksen jälkeen eikä kun käyttäjä valitsi kannan tiedot.
      if (this.skipDraftSave || this.keepDraftOnLeave) return;
      // Lomake ei ehtinyt alustua, joten tallennettavaa ei ole. Uudella merkinnällä alustuksen
      // kertoo defaultEntryTimes, olemassa olevalla kannasta otettu vedos.
      if (this.id ? !this.savedDaybookSnapshot : !this.defaultEntryTimes) return;
      const key = this.draftStorageKey();
      if (!key) return;

      // Koskematonta lomaketta ei kannata tallentaa, muuten kysyisimme siitä turhaan.
      // Olemassa olevalla merkinnällä verrataan kannasta ladattuun tilaan, uudella tyhjään malliin.
      const changed = this.id ? this.hasUnsavedChanges() : this.hasDraftContent();
      if (!changed) {
        localStorage.removeItem(key);
        return;
      }

      const draft = { savedAt: dayjs().toISOString(), ...this.draftSnapshot() };

      try {
        localStorage.setItem(key, JSON.stringify(draft));
      } catch (e) { /* ignore */ }
    },
    // Lomakkeen luonnokseen tallennettava sisältö. Sama metodi sekä tallennukseen että
    // muutosvertailuun, jotta ne eivät voi eriytyä toisistaan.
    draftSnapshot() {
      const d = this.daybook;
      const snapshot = {
        daybookDate: d.daybookDate ? dayjs(d.daybookDate).format(dateFormat) : null,
        workId: (d.work && d.work.id) || null,
        containsLunch: d.containsLunch,
        deviationReportMade: d.deviationReportMade,
        attributeValues: d.attributeValues || [],
        // Kuvia ei voi säilyttää, joten vain rivien tekstit talteen.
        daybookItems: (d.daybookItems || []).filter(item => item.value).map(item => ({ type: item.type, value: item.value })),
      };
      DRAFT_TIME_FIELDS.forEach(field => (snapshot[field] = d[field]));
      DRAFT_DATE_FIELDS.forEach(field => (snapshot[field] = d[field]));
      DRAFT_TEXT_FIELDS.forEach(field => (snapshot[field] = d[field]));
      return snapshot;
    },
    hasUnsavedChanges() {
      if (!this.savedDaybookSnapshot) return false;
      return JSON.stringify(this.draftSnapshot()) !== JSON.stringify(this.savedDaybookSnapshot);
    },
    readDraft() {
      const key = this.draftStorageKey();
      if (!key) return null;
      try {
        const saved = localStorage.getItem(key);
        if (!saved) return null;
        const draft = JSON.parse(saved);
        if (!draft || !draft.savedAt || dayjs().diff(dayjs(draft.savedAt), 'day') >= DRAFT_TTL_DAYS) {
          localStorage.removeItem(key);
          return null;
        }
        return draft;
      } catch (e) {
        // virheellinen arvo localStoragessa – aloitetaan tyhjästä
        return null;
      }
    },
    removeDraft() {
      const key = this.draftStorageKey();
      if (key) localStorage.removeItem(key);
    },
    // Luonnos kuuluu tälle merkinnälle jos päivä ja työ täsmäävät. Merkintä on työkohtainen,
    // joten samalla päivällä ja työllä on kyse samasta merkinnästä riippumatta siitä avattiinko
    // se uutena vai olemassa olevana.
    draftMatchesCurrentDaybook(draft) {
      if (!draft.daybookDate || !this.daybook.daybookDate) return false;
      const sameDate = dayjs(draft.daybookDate).format('DD.MM.YYYY') === this.daybook.daybookDate.format('DD.MM.YYYY');
      return sameDate && (draft.workId || null) === (this.daybook.work?.id || null);
    },
    async handleDraftOnOpen() {
      const draft = this.readDraft();
      if (!draft) return;
      // Olemassa olevaa merkintää avattaessa luonnos tarjotaan vain jos se on saman päivän ja
      // työn keskeneräiset muutokset; muuten kannan tiedot jäävät voimaan.
      if (this.id && !this.draftMatchesCurrentDaybook(draft)) return;
      const confirm = await this.$root.$confirm(
        this.$t('daybook.continue_draft.title'),
        this.id ? this.$t('daybook.continue_draft.description_existing') : this.$t('daybook.continue_draft.description'),
        {
          color: 'primary',
          agree: this.$t('daybook.continue_draft.confirm'),
          cancel: this.id ? this.$t('daybook.continue_draft.cancel_existing') : this.$t('daybook.continue_draft.cancel'),
          // Oletusleveys ei riitä pitkille painiketeksteille, jolloin ne menevät päällekkäin.
          width: 560,
        }
      );
      if (confirm) {
        this.applyDraft(draft);
      } else if (!this.id) {
        // Luonnosta ei poisteta tässä: Esc antaa saman tuloksen kuin painikkeen painallus,
        // eikä vahingossa painettu näppäin saa hävittää viikon työtä. Luonnos jää talteen
        // kunnes käyttäjä täyttää lomaketta uudelleen tai painaa Tyhjennä.
        this.resetDaybookFields();
      } else {
        // Sama peruste olemassa olevalla merkinnällä: lomake jää kannan tilaan, jolloin
        // poistumishook poistaisi luonnoksen muutoksettomana. Jätetään se talteen.
        this.keepDraftOnLeave = true;
      }
    },
    applyDraft(draft) {
      DRAFT_TEXT_FIELDS.forEach(field => {
        if (draft[field] !== undefined) this.$set(this.daybook, field, draft[field]);
      });
      if (draft.containsLunch !== undefined) this.$set(this.daybook, 'containsLunch', draft.containsLunch);
      if (draft.deviationReportMade !== undefined) {
        this.$set(this.daybook, 'deviationReportMade', draft.deviationReportMade);
      }

      // Olemassa olevalla merkinnällä työ ja päivä ovat jo luonnoksen mukaiset (luonnos
      // hyväksyttiin vain jos ne täsmäävät), joten niitä ei aseteta uudelleen: se rakentaisi
      // legacy-porausrivit turhaan ja nostaisi päällekkäisyysdialogin merkinnälle itselleen.
      if (!this.id) {
        // Työ haetaan tuoreena: tallennettu voi olla vanhentunut tai poistettu.
        // Työ ennen päivämäärää, jotta päällekkäisyystarkistus osuu oikeaan työhön.
        const work = (draft.workId && this.works.find(w => w.id === draft.workId)) || null;
        this.applyWorkChange(work);

        // Päivämäärä olemassa olevan käsittelijän kautta: se siirtää alku- ja loppuajat
        // oikealle päivälle, synkronoi kentät ja tarkistaa päällekkäisyydet.
        if (draft.daybookDate) {
          this.handleDaybookDateChange(draft.daybookDate, '');
        }
      }

      // Kellonajat ja päivämäärät vasta päivämäärän jälkeen, ettei synkronointi ylikirjoita niitä.
      // Päivämäärät nimenomaan tässä järjestyksessä: handleDaybookDateChange asettaa alku- ja
      // loppupäiväksi merkinnän päivän, joten yön yli jatkuvan vuoron loppupäivä palautetaan vasta
      // sen jälkeen luonnoksesta.
      [...DRAFT_TIME_FIELDS, ...DRAFT_DATE_FIELDS].forEach(field => {
        if (draft[field] !== undefined) this.$set(this.daybook, field, draft[field]);
      });

      const dayDefIds = this.dayScopeDefs.map(def => def.id);
      this.$set(
        this.daybook,
        'attributeValues',
        (draft.attributeValues || []).filter(v => dayDefIds.includes(v.defId))
      );

      // Rivit rakennetaan uudelleen tyyppi kerrallaan. Aina asetettuna, jotta käyttäjän
      // poistamat rivit eivät palaa; tyhjät tyypit saavat yhden rivin kuten mallissa.
      // Olemassa olevalla merkinnällä tallennetut rivit säilytetään sellaisenaan, jotta
      // niiden id:t ja kuvat eivät katoa; luonnoksesta lisätään vain uudet tekstit.
      const items = [];
      DAYBOOK_ITEM_TYPES.forEach(type => {
        const savedItems = (draft.daybookItems || []).filter(item => item.type === type && item.value);
        const existingItems = this.id
          ? (this.daybook.daybookItems || []).filter(item => item.type === type && (item.id > 0 || item.value))
          : [];
        const existingValues = existingItems.map(item => item.value);
        existingItems.forEach(item => items.push(item));
        savedItems.filter(item => !existingValues.includes(item.value)).forEach(item => items.push({ ...daybookItemTemplate(), type, value: item.value }));
        if (items.filter(item => item.type === type).length === 0) {
          items.push({ ...daybookItemTemplate(), type });
        }
      });
      this.$set(this.daybook, 'daybookItems', items);
    },
    resetDaybookFields() {
      // Työmaa tulee osoitteesta eikä ole osa täytettävää lomaketta, joten se säilytetään.
      const site = this.daybook.site || this.sites[0] || null;
      this.daybook = Immutable.fromJS({ ...daybookTemplate, userId: this.$userInfo?.id }).toJS();
      this.daybook.site = site;
      this.workStatus = null;
      this.syncEntryTimesAndDates(this.daybook);
      this.syncEntryTimesAndDates(this.daybook, 'Wait');
      this.syncEntryTimesAndDates(this.daybook, 'Wait2');
      this.syncEntryTimesAndDates(this.daybook, 'Wait3');
      // Uusi merkintä käyttää vain uutta mallia (työ + AttributeForm); ei luoda legacy-porausrivejä.
      // Oletusajat talteen, jotta koskematonta lomaketta ei tallenneta luonnokseksi.
      this.defaultEntryTimes = this.currentEntryTimes();
    },
    async clearDaybook() {
      const confirm = await this.$confirm('warning', this.$t('daybook.clear_daybook'), this.$t('daybook.clear_daybook_confirm'));
      if (!confirm) return;
      this.removeDraft();
      this.resetDaybookFields();
    },
    async saveDaybook() {
      /*if (
        !(
          (this.daybook.drillingLengthAtDay === 0 || this.daybook.drillingLengthAtDay === null) ||
          this.daybook.site?.minDrillingLength === null ||
          this.daybook.drillingLengthAtDay >= this.daybook.site?.minDrillingLength
        )
      ) {
        this.$showErrorNotification(this.$t('daybook.drilling_length_validation_error'));
        return;
      }*/

      // Lopetuspäivä on käyttäjän valittavissa, joten se voi mennä aloituksen edelle.
      // Kestolaskenta näyttäisi käänteisen välin positiivisena, joten estetään tallennus.
      if (
        this.daybook.entryStart &&
        this.daybook.entryEnd &&
        dayjs(this.daybook.entryEnd).isBefore(dayjs(this.daybook.entryStart))
      ) {
        this.$showErrorNotification(this.$t('daybook.entry_end_before_start'));
        return;
      }

      // Uudella merkinnällä työn valinta on pakollinen.
      if (!(this.daybook.id > 0) && !this.daybook.work) {
        this.$showErrorNotification('Valitse työ päiväkirjamerkinnälle.');
        return;
      }

      // Tallennusnappi vie fokuksen kentästä, mutta blurin @change ehtii ajoon vasta tämän
      // jälkeen. Odotetaan se, jotta kesken kirjoitettu Yhteensä ehtii sitoutua tai hylätä.
      document.activeElement?.blur();
      await this.$nextTick();
      if (this.$refs.attributeForm?.hasErrors()) {
        this.$showErrorNotification(this.$t('daybook.attribute_total_below_base_save'));
        return;
      }

      this.calculateTotalLengths();
      this.calculateTotalPipeLengths();
      if (!this.checkIfAddedDaybookItemImageWithoutValue('deviationDaybookItems')) return;
      if (!this.checkIfAddedDaybookItemImageWithoutValue('boughtDaybookItems')) return;
      if (!this.checkIfAddedDaybookItemImageWithoutValue('orderedDaybookItems')) return;
      this.saving = true;

      let daybook = Immutable.fromJS(this.daybook).toJS();
      console.log(daybook);
      daybook.daybookItems = daybook.daybookItems?.filter(d => d.id || d.value);
      /*daybook.daybookDrillings = daybook.daybookDrillings?.filter(
        d => d.drillingLengthAtDay !== null || d.ofWhichRock !== null || d.elevationTopEnd !== null || d.elevationLowerEnd !== null
      );*/
      if (
        ((daybook.entryStartWait && dayjs(daybook.entryStartWait).format('HHmm') === '0000') || daybook.entryStartWait2 === null) &&
        !daybook.entryEndWait
      ) {
        daybook.entryStartWait = null;
      }
      if (
        ((daybook.entryStartWait2 && dayjs(daybook.entryStartWait2).format('HHmm') === '0000') || daybook.entryStartWait2 === null) &&
        !daybook.entryEndWait2
      ) {
        daybook.entryStartWait2 = null;
      }
      if (
        ((daybook.entryStartWait3 && dayjs(daybook.entryStartWait3).format('HHmm') === '0000') || daybook.entryStartWait3 === null) &&
        !daybook.entryEndWait3
      ) {
        daybook.entryStartWait3 = null;
      }
      try {
        if (daybook.id > 0) {
          daybook = await daybookApi.updateDaybook(this.id, daybook); // CTRL+P näytttää mitä pitää laittaa
          this.$showSuccessNotification(this.$t('daybook.saved'));
        } else {
          // Päällekkäisyys tarkistetaan tässä erikseen virheilmoituksella, joten ei nosteta dialogia.
          await this.getDaybooks(false);

          const newDate = this.daybook.daybookDate.format('DD.MM.YYYY');
          const newWorkId = this.daybook.work?.id || null;
          const conflict = this.daybooks.some(
            d => d.daybookDate.format('DD.MM.YYYY') === newDate && (d.work?.id || null) === newWorkId
          );
          if (conflict) {
            this.$showErrorNotification(this.$t('daybook.daybook_exists_error'));
            this.saving = false;
            return;
          }

          daybook = await daybookApi.createDaybook(daybook);
          this.$showSuccessNotification(this.$t('daybook.created'));
        }

        // Kuvat lähetetään ennen navigointia: reitin vaihto tuhoaa näkymän ja sen refit,
        // jolloin uuden merkinnän puskuroidut kuvat jäisivät lähettämättä.
        await this.uploadDaybookItemImages(daybook, 'deviationDaybookItems');
        await this.uploadDaybookItemImages(daybook, 'boughtDaybookItems');
        await this.uploadDaybookItemImages(daybook, 'orderedDaybookItems');
        if (this.$refs.daybookImages) await this.$refs.daybookImages.uploadFiles(daybook.id);
        await this.saveWorkStatus(daybook);

        // Luonnos pois, ja lippu päälle ettei poistumishook tallenna sitä takaisin.
        this.removeDraft();
        this.skipDraftSave = true;
        await this.$router.push(this.afterSaveRoute(daybook));
      } catch (e) {
        console.error(e);
        this.$showErrorNotification(this.$t('generic_error'));
        this.saving = false;
        return;
      }
      //await this.getDaybook();
      this.saving = false;
      //await this.$router.go(-1);
    },
    // Työn tila päivitetään omalla rajapinnalla vasta merkinnän tallennuttua, jos se muuttui.
    // Epäonnistuminen ei estä paluuta: merkintä on jo tallessa.
    async saveWorkStatus(daybook) {
      const work = daybook.work;
      if (!this.canChangeWorkStatus || !work || !this.workStatus || this.workStatus === work.status) return;
      try {
        await siteApi.updateSiteWorkStatus(this.siteId, work.id, this.workStatus);
        this.$showSuccessNotification(this.$t('sites.work_status_changed'));
      } catch (e) {
        console.error(e);
        this.$showErrorNotification(this.$t('sites.status_change_failed'));
      }
    },
    // Tallennuksen jälkeen palataan tuloreitille; oletuksena Päiväkirjat-välilehdelle
    // ja vieritetään tallennettuun merkintään.
    afterSaveRoute(daybook) {
      return (
        this.resolvedOriginRoute ||
        { name: 'tenant_site_edit_site', params: { id: daybook.site.id, tab: 'daybooks' }, hash: '#daybook-' + daybook.id }
      );
    },
    async cancel() {
      await this.$router.go(-1);
    },
    async getDaybook() {
      this.loading = true;

      const daybook = await daybookApi.getDaybook(this.id);
      this.syncEntryTimesAndDates(daybook);
      this.syncEntryTimesAndDates(daybook, 'Wait');
      this.syncEntryTimesAndDates(daybook, 'Wait2');
      this.syncEntryTimesAndDates(daybook, 'Wait3');
      if ((daybook.entryStartWait2 && dayjs(daybook.entryStartWait2).format('HHmm') !== '0000') || daybook.entryEndWait2 !== null) {
        this.waitTimes = 2;
      }
      if ((daybook.entryStartWait3 && dayjs(daybook.entryStartWait3).format('HHmm') !== '0000') || daybook.entryEndWait3 !== null) {
        this.waitTimes = 3;
      }
      this.daybook = daybook;
      this.workStatus = (daybook.work && daybook.work.status) || null;

      this.addDaybookDrillings();

      // Vedos vasta rivien täydennyksen jälkeen, jotta se vastaa lomakkeen lähtötilaa.
      this.savedDaybookSnapshot = this.draftSnapshot();

      this.loading = false;
    },
    async addDaybookDrillings() {
      const relevantDrillings = this.daybook.work
        ? this.siteDrillings.filter(d => d.taskOrder === this.daybook.work.sortOrder)
        : this.siteDrillings;
      relevantDrillings.forEach(drilling => {
        const d = this.daybook.daybookDrillings.find(dd => dd.taskOrder === drilling.taskOrder);
        if (!d) {
          if (this.daybook.daybookDrillings === null) {
            this.daybook.daybookDrillings = [];
          }
          this.daybook.daybookDrillings.push({
            ...Immutable.fromJS(daybookDrillingTemplate).toJS(),
            taskOrder: drilling.taskOrder,
            method: drilling.method?.item,
            diameter: drilling.pipe?.item,
            rearingSize: drilling.rearingSize?.item,
          });
        } else {
          this.$set(d, 'method', drilling.method?.item);
          this.$set(d, 'diameter', drilling.pipe?.item);
          this.$set(d, 'rearingSize', drilling.rearingSize?.item);
        }
      });
    },
    async getItems() {
      this.workMethods = await workMethodsApi.getWorkMethods();
    },
    async getPersons() {
      this.persons = await personApi.getPersons();
    },
    async getSites() {
      this.sites = [await siteApi.getSite(this.siteId)];
      if (!this.daybook.site) this.daybook.site = this.sites[0];
      /*if (!this.daybook.method && this.daybook.site && this.daybook.site.methods && this.daybook.site.methods.length > 0) {
        this.daybook.method = this.daybook.site.methods[0];
      }
      if (!this.daybook.diameter && this.daybook.site && this.daybook.site.diameters && this.daybook.site.diameters.length > 0) {
        this.daybook.diameter = this.daybook.site.diameters[0];
      }*/
    },

    async getWorkGroups() {
      this.workGroups = await workGroupApi.getWorkGroups();
    },
    async getEquipments() {
      this.equipments = await equipmentApi.getAllEquipments();
    },
    async getProjects() {
      this.projects = await projectApi.getProjects();
    },
    async getDaybooks(checkExisting = true) {
      this.daybooks = await daybookApi.getDaybooksBySiteId(this.siteId);
      if (checkExisting) this.checkExistingDaybookForDate();
    },
    checkExistingDaybookForDate(trigger = 'date') {
      if (this.$route.name !== 'tenant_site_new_daybook') return;
      if (!this.daybook.daybookDate) return;
      const existingDaybook = this.daybooks.find(
        d =>
          d.daybookDate.format('DD.MM.YYYY') === this.daybook.daybookDate.format('DD.MM.YYYY') &&
          (d.work?.id || null) === (this.daybook.work?.id || null)
      );
      if (existingDaybook) {
        this.existingDaybookId = existingDaybook.id;
        this.existingDaybookTrigger = trigger;
        this.existingDaybookDialog = true;
      }
    },
    goToExistingDaybook() {
      this.existingDaybookDialog = false;
      // Reload pyyhkii originRouten, joten talletetaan se sessioon paluuta varten.
      if (this.originRoute) {
        try {
          sessionStorage.setItem(ORIGIN_ROUTE_STORAGE_KEY, JSON.stringify(this.originRoute));
        } catch (e) { /* ignore */ }
      }
      // Luonnos tallennetaan poistumishookissa normaalisti, jotta olemassa oleva merkintä
      // voi tarjota keskeneräiset muutokset takaisin.
      this.$router.push({ name: 'tenant_site_daybook', params: { siteId: this.siteId, id: this.existingDaybookId } }).then(() => {
        window.location.reload();
      });
    },
    cancelExistingDaybook() {
      // Palautetaan se valinta joka päällekkäisyyden aiheutti, jotta käyttäjä voi valita toisin.
      // previousDaybookDate on null jos dialogi nousi muusta kuin päivämäärän vaihdosta.
      if (this.existingDaybookTrigger === 'work') {
        this.$set(this.daybook, 'work', this.previousWork);
        this.workSelectKey++;
      } else if (this.previousDaybookDate) {
        this.daybook.daybookDate = this.previousDaybookDate;
      }
      this.existingDaybookDialog = false;
    },
    async getWorkers() {
      this.workers = (await workerApi.getWorkers()).map(worker => {
        worker.workerId = worker.id;
        return worker;
      });
    },
    async onWorkGroupChange(workGroup) {
      if (this.site.workers?.length === 0 && workGroup && workGroup.workers) {
        this.site.workers = [...workGroup.workers];
        this.$showSuccessNotification(this.$t('sites.messages.workers_added'));
      }
    },
    async deleteWorker(worker) {
      const wIndex = this.site.workers.findIndex(w => w.workerId === worker.workerId);
      if (wIndex > -1) this.site.workers.splice(wIndex, 1);
    },
    calculateTotalLengths() {
      this.daybook.daybookDrillings.forEach(dd => {
        dd.drillingLength =
          this.daybooks
            .map(daybook => {
              if (dayjs(daybook.daybookDate).isBefore(this.daybook.daybookDate)) {
                return daybook.daybookDrillings
                  .filter(_dd => _dd.taskOrder === dd.taskOrder && _dd.drillingLengthAtDay > 0)
                  .map(_dd => _dd.drillingLengthAtDay)
                  .filter(Boolean)
                  .reduce((c, p) => parseFloat(p) + c, 0);
              }
              return null;
            })
            .filter(Boolean)
            .reduce((c, p) => parseFloat(p) + c, 0) + parseFloat(dd.drillingLengthAtDay || 0);
      });
    },
    calculateTotalPipeLengths() {
      this.daybook.daybookDrillings.forEach(dd => {
        dd.pipeLength =
          this.daybooks
            .map(daybook => {
              if (dayjs(daybook.daybookDate).isBefore(this.daybook.daybookDate)) {
                return daybook.daybookDrillings
                  .filter(_dd => _dd.taskOrder === dd.taskOrder && _dd.pipeLengthAtDay > 0)
                  .map(_dd => _dd.pipeLengthAtDay)
                  .filter(Boolean)
                  .reduce((c, p) => parseFloat(p) + c, 0);
              }
              return null;
            })
            .filter(Boolean)
            .reduce((c, p) => parseFloat(p) + c, 0) + parseFloat(dd.pipeLengthAtDay || 0);
      });
    },
  },
  watch: {
    // Kaikki polut (uusi merkintä, haku, päivän/työn vaihto, luonnoksen palautus, tyhjennys) kulkevat tätä kautta.
    'timeEntryWorkersQuery.key': {
      handler() {
        this.loadTimeEntryWorkers();
      },
      immediate: true,
    },
    'daybook.entryStartDate'() {
      this.createChangedEntryDate('start');
    },
    'daybook.entryStartTime'() {
      this.createChangedEntryDate('start');
    },
    'daybook.entryEndDate'() {
      this.createChangedEntryDate('end');
    },
    'daybook.entryEndTime'() {
      this.createChangedEntryDate('end');
    },

    'daybook.entryStartDateWait'() {
      this.createChangedEntryDate('start', 'Wait');
    },
    'daybook.entryStartTimeWait'() {
      this.createChangedEntryDate('start', 'Wait');
    },
    'daybook.entryEndDateWait'() {
      this.createChangedEntryDate('end', 'Wait');
    },
    'daybook.entryEndTimeWait'() {
      this.createChangedEntryDate('end', 'Wait');
    },

    'daybook.entryStartDateWait2'() {
      this.createChangedEntryDate('start', 'Wait2');
    },
    'daybook.entryStartTimeWait2'() {
      this.createChangedEntryDate('start', 'Wait2');
    },
    'daybook.entryEndDateWait2'() {
      this.createChangedEntryDate('end', 'Wait2');
    },
    'daybook.entryEndTimeWait2'() {
      this.createChangedEntryDate('end', 'Wait2');
    },

    'daybook.entryStartDateWait3'() {
      this.createChangedEntryDate('start', 'Wait3');
    },
    'daybook.entryStartTimeWait3'() {
      this.createChangedEntryDate('start', 'Wait3');
    },
    'daybook.entryEndDateWait3'() {
      this.createChangedEntryDate('end', 'Wait3');
    },
    'daybook.entryEndTimeWait3'() {
      this.createChangedEntryDate('end', 'Wait3');
    },
  },
};
</script>

<style scoped>
.button_group {
  position: fixed;
  bottom: 16px;
  right: 16px;
}

.grey--bg {
  background-color: var(--v-default-lighten1);
}

/* Tekstikentän prepend-ikoni 24px + sen marginaali 9px. */
.daybook-field-column {
  margin-left: 33px;
}
/* Valintaruudun 20px glyfi piirtyy 24px ikonin sisällä noin 4px sisennettynä; ruudun reuna samaan linjaan tekstien kanssa. */
.daybook-field-column--checkbox {
  margin-left: 29px;
}
</style>
